import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/contexts/AuthContext';
import styles from './LoginPage.module.css';

const ADMIN_PORTAL_URL = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:3000';

export default function LoginPage() {
  const { firebaseUser, platformUser, loading, error, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard when logged in
  useEffect(() => {
    if (!loading && firebaseUser && platformUser) {
      navigate({ to: '/dashboard' });
    }
  }, [loading, firebaseUser, platformUser, navigate]);

  return (
    <div className={styles.root}>
      {/* Background gradient blobs */}
      <div className={styles.blob1} />
      <div className={styles.blob2} />

      <div className={styles.card}>
        {/* Logo */}
        <div className={styles.logo}>
          <span className={styles.logoMark}>e</span>
          <span className={styles.logoText}>enska.ai</span>
        </div>

        <h1 className={styles.headline}>Welcome back</h1>
        <p className={styles.sub}>
          Sign in to manage your AI agents and conversations.
        </p>

        {/* SuperAdmin redirect error */}
        {error === 'superadmin' && (
          <div className={styles.superAdminNotice}>
            <span className={styles.noticeIcon}>🔐</span>
            <div>
              <strong>Staff account detected</strong>
              <p>enska.ai staff accounts use the <a href={ADMIN_PORTAL_URL}>admin portal</a> instead. Redirecting you there now…</p>
            </div>
          </div>
        )}

        {/* Generic error */}
        {error && error !== 'superadmin' && (
          <div className={styles.errorBanner}>{error}</div>
        )}

        <button
          id="google-sign-in"
          className={styles.googleBtn}
          onClick={signInWithGoogle}
          disabled={loading}
        >
          {loading ? (
            <span className={styles.spinner} />
          ) : (
            <>
              <svg className={styles.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <p className={styles.disclaimer}>
          By signing in you agree to enska.ai's{' '}
          <a href="https://enska.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>{' '}
          and{' '}
          <a href="https://enska.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <div className={styles.adminLink}>
          enska.ai staff?{' '}
          <a href={ADMIN_PORTAL_URL}>Use the admin portal →</a>
        </div>
      </div>
    </div>
  );
}
